import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import Select from "react-select";
import { IBigscreenSettings, IEventField } from "../../../../constants/interfaces";
import {sortAlphabetically} from "../../helper/formatters";
import useEventFields from "../../../../hooks/useEventFields";

const metrics = [
	{label: "Page views", value: "pageviews"},
	{label: "Web page views", value: "webpageviews"},
	{label: "App page views", value: "apppageviews"},
	{label: "Conversions", value: "conversion"},
	{label: "Stories read total", value: "totalstories"},
	{label: "Tests performed total", value: "tests"},
	{label: "Sessions total", value: "sessions"},
	{label: "Active users total", value: "activeUsers"},
	{label: "Video views total", value: "videoPageViews"},
	{label: "Avg. reading time", value: "averageReadingTime"},
	{label: "Total reading time", value: "totalReadingTime"},
	{label: "Avg. read depth", value: "averageReadDepth"},
	{label: "Avg. scroll depth", value: "averageScrollDepth"},
	{label: "Notifications total", value: "notificationsTotal"},
	{label: "Total visitors", value: "totalVisitors"},
	{label: "Published stories", value: "publishedStories"},
	{label: "Web visitors", value: "webVisitors"},
	{label: "App visitors", value: "appVisitors"},
	{label: "Social actions", value: "socialActions"},
];

const sortedMetrics = sortAlphabetically(metrics, "label");

type Props = {
	initialValue: string | null,
	updateConfig: Function,
	bigscreenSettings?: IBigscreenSettings[],
	isLoadingBigscreenSettings: boolean,
	initialEventFields: IEventField[]
}

type Option = {
	value: string,
	label: string
}

const usePrevious = <T extends unknown>(value: T): T | undefined => {
	const ref = useRef<T>();
	useEffect(() => {
		ref.current = value;
	});
	return ref.current;
};

const Type = ({ initialValue, bigscreenSettings, isLoadingBigscreenSettings, initialEventFields, updateConfig}: Props) => {
	const [value, setValue] = useState(initialValue !== 'totalevents' ? initialValue : (initialEventFields.length ? initialEventFields[0].name : null));
	const eventFields = useEventFields(bigscreenSettings);

	useEffect(() => {
		if (value === "activeUsers") {
			updateConfig(['content_type', 'time_window', 'event_fields'], [value, "1m", []]);
		} else if (value !== initialValue && value === "publishedStories") {
			updateConfig(['content_type', 'time_window', 'pubdate_interval', 'event_fields'], [value, "1m", 'midnight', []]);
		} else if (eventFields && eventFields.some(eventField => eventField.name === value)) {
			updateConfig(['content_type', 'event_fields'], ['totalevents', [eventFields.find(eventField => eventField.name === value)]]);
		} else {
			updateConfig(['content_type', 'event_fields'], [value, []]);
		}
	}, [value, initialValue, eventFields, updateConfig]);

	const getSortedOptions = useCallback(() => {
		const metricList = [...sortedMetrics];
		
		if (eventFields !== undefined) {
			eventFields.forEach(eventField => {
				metricList.push({
					value: eventField.name,
					label: eventField.label
				});
			});
		}

		return sortAlphabetically(metricList, "label");
	}, [eventFields]);

	const type = useMemo(() => {
		if (!value) {
			return sortedMetrics[0];
		}

		return getSortedOptions().find((metric: Option) => metric.value === value);
	}, [value, getSortedOptions]);
	
	const prevEventFields = usePrevious(eventFields);
	// Reset sort to default value if column is no longer available (e.g. brand is deselected)
	useEffect(() => {
		if ( 
			prevEventFields
			&& prevEventFields.find(eventField => eventField.name === value)
			&& eventFields && !eventFields.find(eventField => eventField.name === value)
		) {
			setValue("pageviews");
		}
	}, [prevEventFields, eventFields, value]);

	return (
		<div className="config-item__selects">
			<div className="form-group">
				<label htmlFor="metricType">Type:</label>
				<Select
					id="metricType"
					className="basic-single"
					classNamePrefix="so-select-metric-type"
					isSearchable={true}
					isLoading={isLoadingBigscreenSettings}
					value={type}
					options={getSortedOptions()}
					onChange={(option: Option | null) => setValue(option!.value)}
					menuPortalTarget={document.body}
					styles={{menuPortal: base => ({...base, zIndex: 9999})}}
				/>
			</div>
		</div>
	);
};

Type.defaultProps = {
	initialEventFields: [],
	isLoadingBigscreenSettings: false,
	initialValue: null
};

const mapStateToProps = (state: any) => {
	return {
		isLoadingBigscreenSettings: state.bigscreenSettings ? state.bigscreenSettings.isLoading : {}
	}
};

export default connect(mapStateToProps)(Type);
